'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu';
import React, { ReactNode, useEffect, useState } from 'react';
import { Button } from '@workspace/ui/components/button';
import { IUINotification } from '@/src/types/interfaces';
import { useBoolean } from '@/src/hooks/use-boolean';
import { Notification } from './Notification';
import {
  deleteAllNotifications,
  getNotifications,
} from '@/src/actions/notifications';
import { LoadingOverlay } from '@/src/sections/profile/components/custom-grid/LoadingOverlay';
import { IUIProfile } from '@/src/types/IUIProfile';
import { supabase } from '@/src/lib/supabase/client';

type Props = {
  children: ReactNode;
  isNotificationsOpen: ReturnType<typeof useBoolean>;
  profile: IUIProfile;
  onAllRead?: () => void;
  onAnyRead?: () => void;
};

export const ProfileNotifications = ({
  children,
  isNotificationsOpen,
  profile,
  onAllRead,
  onAnyRead,
}: Props) => {
  const [notifications, setNotifications] = useState<IUINotification[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (profile && profile.type === 'anonymous') return;
    (async () => {
      setLoading(true);
      try {
        const { notifications: raw, error } = await getNotifications();
        if (error) {
          if (!cancelled) setNotifications([]);
        } else {
          const list = Array.isArray(raw) ? raw : [];
          if (!cancelled) setNotifications(list);
        }
      } catch (e) {
        console.error('getNotifications failed', e);
        if (!cancelled) setNotifications([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNotificationsOpen.value]);

  useEffect(() => {
    if (!profile) return;
    if (profile.type === 'anonymous') return;
    if (!isNotificationsOpen.value) return;

    const channel = supabase
      .channel(`rt:notifications:${profile.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `owner_id=eq.${profile.id}`,
        },
        (payload) => {
          const row = payload.new as any;
          if (row?.is_new === false) return;

          const next = row as IUINotification;

          setNotifications((prev) => {
            if (prev.some((x) => x.id === next.id)) return prev;
            return [next, ...prev];
          });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [isNotificationsOpen.value, profile?.id]);

  const handleDeleteAll = async () => {
    const { success, error } = await deleteAllNotifications();
    if (error) {
      console.error('Error deleting all notifications:', error);
    }
    if (success) {
      setNotifications([]);
      onAllRead?.();
    }
  };

  const hasNotifications =
    Array.isArray(notifications) && notifications.length > 0;

  return (
    <DropdownMenu
      modal={false}
      open={isNotificationsOpen.value}
      onOpenChange={(open) => isNotificationsOpen.setValue(open)}
    >
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        className="p-0 md:w-100 w-[300px] h-[470px] relative"
      >
        <div className="flex items-center justify-between py-2 px-4">
          <h3 className="font-medium md:text-lg text-base">Notifications</h3>

          {hasNotifications && (
            <Button
              size="sm"
              variant="outline"
              className="cursor-pointer px-2 bg-white"
              onClick={handleDeleteAll}
            >
              Mark all as read
            </Button>
          )}
        </div>
        <DropdownMenuSeparator className="m-0" />

        {loading ? (
          <LoadingOverlay />
        ) : hasNotifications ? (
          notifications.map((n) => (
            <Notification
              key={n.id}
              notification={n}
              onRead={() => {
                onAnyRead?.();
                setNotifications((prev) => prev.filter((x) => x.id !== n.id));
              }}
            />
          ))
        ) : (
          <div className="w-full text-sm font-medium text-black/40 my-44 text-center">
            All notifications were marked as read
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
